const NARROW_BREAKPOINT = 360;
const FOOTER_PADDING = '110px';

class PdpFloatingCta extends HTMLElement {
  constructor() {
    super();
    this.onScrollOrResize = this.onScrollOrResize.bind(this);
    this.onOptionInputChange = this.onOptionInputChange.bind(this);
    this.updateCtaState = this.updateCtaState.bind(this);
    this.onVariantChangeEvent = this.onVariantChangeEvent.bind(this);
    this.onSellingPlanChange = this.onSellingPlanChange.bind(this);
    this.onPlanInputChange = this.onPlanInputChange.bind(this);
    this.onPlanToggle = this.onPlanToggle.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onCartSettled = this.onCartSettled.bind(this);
    this.isAdding = false;
    this.variants = [];
    this.productTags = [];
    this.labels = {};
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.productTags = (this.dataset.productTags || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
    // No hardcoded English fallbacks: every label is rendered from a locale
    // key into these data attributes by the paired snippet. A missing
    // attribute means a locale key is blank, not a reason to show English.
    this.labels = {
      add: this.dataset.labelAdd,
      adding: this.dataset.labelAdding,
      soldOut: this.dataset.labelSoldOut,
      notify: this.dataset.labelNotify,
      amazon: this.dataset.labelAmazon,
      planSubscribe: this.dataset.labelPlanSubscribe,
      planOneTime: this.dataset.labelPlanOneTime,
      planOneTimeDetail: this.dataset.labelPlanOneTimeDetail,
    };

    this.mainCta = document.getElementById(`add-to-cart-container-${this.sectionId}`);
    this.optionInputs = this.querySelectorAll('[data-floating-option-input]');
    this.atcButton = this.querySelector('[data-floating-atc]');
    this.atcText = this.querySelector('[data-floating-atc-text]');
    this.externalLink = this.querySelector('[data-floating-external]');
    this.bisLink = this.querySelector('[data-floating-bis]');
    this.subtitleEl = this.querySelector('[data-floating-subtitle]');
    this.imageEl = this.querySelector('[data-floating-image], img.floatingCTA__image');
    this.plan = this.querySelector('[data-floating-plan]');
    this.planTrigger = this.querySelector('[data-floating-plan-trigger]');
    this.planLabel = this.querySelector('[data-floating-plan-label]');
    this.planMenu = this.querySelector('[data-floating-plan-menu]');
    this.planInputs = this.querySelectorAll('[data-floating-plan-input]');
    this.footer = document.querySelector('.footer');

    try {
      const jsonEl = this.querySelector('[data-floating-variants]');
      this.variants = jsonEl ? JSON.parse(jsonEl.textContent) : [];
    } catch {
      this.variants = [];
    }

    this.boundForm = this.form;
    this.boundSellingPlansWidget = this.sellingPlansWidget;

    for (const input of this.optionInputs) {
      input.addEventListener('change', this.onOptionInputChange);
    }
    for (const input of this.planInputs) {
      input.addEventListener('change', this.onPlanInputChange);
    }
    this.planMenu?.addEventListener('toggle', this.onPlanToggle);
    this.boundForm?.addEventListener('submit', this.onFormSubmit);
    this.boundSellingPlansWidget?.addEventListener('change', this.onSellingPlanChange);
    this.boundSellingPlansWidget?.addEventListener('click', this.onSellingPlanChange);
    document.addEventListener('product:variant-change', this.onVariantChangeEvent);
    document.addEventListener('cart:change', this.onCartSettled);
    document.addEventListener('cart:error', this.onCartSettled);

    window.addEventListener('scroll', this.onScrollOrResize, { passive: true });
    window.addEventListener('resize', this.onScrollOrResize);

    // The main button's price is rewritten by selling plans, stack & save and
    // variant changes; mirroring it keeps the bar's price from ever drifting.
    this.mainAtcObserver = new MutationObserver(this.updateCtaState);
    if (this.mainAtcButton) {
      this.mainAtcObserver.observe(this.mainAtcButton, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['hidden', 'disabled'],
      });
    }

    this.syncFromProduct();
    this.onScrollOrResize();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollOrResize);
    window.removeEventListener('resize', this.onScrollOrResize);
    for (const input of this.optionInputs) {
      input.removeEventListener('change', this.onOptionInputChange);
    }
    for (const input of this.planInputs) {
      input.removeEventListener('change', this.onPlanInputChange);
    }
    this.planMenu?.removeEventListener('toggle', this.onPlanToggle);
    this.mainAtcObserver?.disconnect();
    this.boundForm?.removeEventListener('submit', this.onFormSubmit);
    this.boundSellingPlansWidget?.removeEventListener('change', this.onSellingPlanChange);
    this.boundSellingPlansWidget?.removeEventListener('click', this.onSellingPlanChange);
    document.removeEventListener('product:variant-change', this.onVariantChangeEvent);
    document.removeEventListener('cart:change', this.onCartSettled);
    document.removeEventListener('cart:error', this.onCartSettled);
    this.setFooterPadding(false);
  }

  get productInfo() {
    return this.closest('product-info');
  }

  get form() {
    const formId = this.dataset.productForm || `product-form-${this.sectionId}`;
    return document.getElementById(formId);
  }

  get variantSelector() {
    return document.getElementById(`variant-selector-${this.sectionId}`);
  }

  get sellingPlansWidget() {
    return this.productInfo?.querySelector('selling-plans-widget');
  }

  get selectedVariantId() {
    return this.form?.querySelector('input[name="id"]')?.value || '';
  }

  get selectedVariant() {
    if (!this.selectedVariantId) return this.variants[0] || null;
    return this.variants.find((v) => String(v.id) === String(this.selectedVariantId)) || null;
  }

  get mainAtcButton() {
    return document.getElementById(`AddToCart-${this.sectionId}`);
  }

  shouldShowBis(variant) {
    if (!variant) return false;
    if (variant.availability === 'external') return false;
    if (variant.availability === 'oos') return true;
    if (!variant.available) {
      if (this.productTags.includes('bis-hidden') || this.productTags.includes('g1mclub')) {
        return false;
      }
      return true;
    }
    return false;
  }

  onOptionInputChange(event) {
    const input = event.currentTarget;
    if (!input.checked) return;

    const optionValueId = input.dataset.optionValueId;
    const productUrl = input.dataset.productUrl;
    const selector = this.variantSelector;
    if (!selector || !optionValueId) return;

    const escapedId = CSS.escape(optionValueId);
    const mainOption = selector.querySelector(`option[data-option-value-id="${escapedId}"]`);
    if (mainOption) {
      const mainSelect = mainOption.closest('select');
      mainSelect.value = mainOption.value;
      if (productUrl) mainSelect.dataset.productUrl = productUrl;
      else delete mainSelect.dataset.productUrl;
      mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const radio = selector.querySelector(`input[data-option-value-id="${escapedId}"]`);
    if (radio) {
      radio.checked = true;
      if (productUrl) radio.dataset.productUrl = productUrl;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  onVariantChangeEvent(event) {
    if (event.detail?.sectionId && event.detail.sectionId !== this.sectionId) return;
    this.syncFromProduct();
  }

  onSellingPlanChange() {
    this.updateSubtitle();
    this.syncPlan();
  }

  /**
   * The bar's purchase option picks the matching radio in the main
   * selling-plans widget. Clicking it goes through the widget's own change
   * handling, so the plan, the main price and the add-to-cart label all update
   * as if the shopper had chosen it there; the bar then mirrors them back.
   */
  onPlanInputChange(event) {
    const input = event.currentTarget;
    if (!input.checked) return;

    const radio = this.planForm?.querySelector(`input[data-radio-type="${CSS.escape(input.value)}"]`);
    if (radio && !radio.checked && !radio.disabled) radio.click();

    this.planMenu?.hidePopover?.();
    this.syncPlan();
  }

  /* The menu is a popover in the top layer, so it is placed from the trigger:
     left-aligned with it and opening upwards, clear of the bar. */
  onPlanToggle(event) {
    const open = event.newState === 'open';
    this.planTrigger?.classList.toggle('is-open', open);
    if (!open || !this.planTrigger) return;

    const rect = this.planTrigger.getBoundingClientRect();
    this.planMenu.style.left = `${rect.left}px`;
    this.planMenu.style.bottom = `${window.innerHeight - rect.top + 8}px`;
  }

  onFormSubmit() {
    this.isAdding = true;
    this.updateCtaState();
  }

  onCartSettled() {
    this.isAdding = false;
    this.updateCtaState();
  }

  onScrollOrResize() {
    if (!this.mainCta) {
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');
      this.setFooterPadding(false);
      return;
    }

    const show = this.mainCta.getBoundingClientRect().top < 0;
    this.classList.toggle('is-visible', show);
    // The menu lives in the top layer, so it would stay on screen after the bar fades out.
    if (!show && this.planMenu?.matches(':popover-open')) this.planMenu.hidePopover();
    this.setAttribute('aria-hidden', show ? 'false' : 'true');

    const isNarrow = window.innerWidth <= NARROW_BREAKPOINT;
    this.classList.toggle('floatingCTAFloat--narrow', isNarrow && show);
    this.setFooterPadding(isNarrow && show);
  }

  setFooterPadding(enabled) {
    if (!this.footer) return;
    if (enabled) {
      this.footer.style.paddingBottom = FOOTER_PADDING;
    } else if (this.footer.style.paddingBottom === FOOTER_PADDING) {
      this.footer.style.paddingBottom = '';
    }
  }

  syncFromProduct() {
    this.syncOptionInputs();
    this.updateImage();
    this.updateSubtitle();
    this.syncPlan();
    this.updateCtaState();
  }

  syncOptionInputs() {
    const selector = this.variantSelector;
    if (!selector) return;

    for (const input of this.optionInputs) {
      const escapedId = CSS.escape(input.dataset.optionValueId);
      const mainOption = selector.querySelector(`option[data-option-value-id="${escapedId}"]`);
      const mainRadio = selector.querySelector(`input[data-option-value-id="${escapedId}"]`);
      if (mainOption?.selected || mainRadio?.checked) input.checked = true;
    }
  }

  updateImage() {
    const variant = this.selectedVariant;
    if (!this.imageEl || this.imageEl.tagName !== 'IMG' || !variant?.image) return;
    this.imageEl.src = variant.image;
    this.imageEl.srcset = `${variant.image} 240w`;
    if (variant.title) this.imageEl.alt = variant.title;
  }

  /** The selected variant's purchase options in the main selling-plans widget. */
  get planForm() {
    const variantId = this.selectedVariantId;
    const widget = this.sellingPlansWidget;
    return (
      (variantId && widget?.querySelector(`.selling_plan_theme_integration[data-variant-id="${CSS.escape(variantId)}"]`)) ||
      widget?.querySelector('.selling_plan_theme_integration:not(.selling_plan_theme_integration--hidden)') ||
      null
    );
  }

  /**
   * Mirrors the main widget's purchase options into the bar's dropdown: which
   * one is chosen, the trigger's label, and each option's price line. Hidden
   * for a variant with no subscription.
   */
  syncPlan() {
    if (!this.plan) return;

    const planForm = this.planForm;
    const subscribe = planForm?.querySelector('input[data-radio-type="selling_plan"]');
    const oneTime = planForm?.querySelector('input[data-radio-type="one_time_purchase"]');

    this.plan.hidden = !subscribe;
    if (!subscribe) {
      if (this.planMenu?.matches(':popover-open')) this.planMenu.hidePopover();
      return;
    }

    const chosen = oneTime?.checked ? 'one_time_purchase' : 'selling_plan';
    const details = {
      selling_plan: [subscribe.dataset.savePercentLabel, subscribe.dataset.variantPrice].filter(Boolean).join(' • '),
      one_time_purchase: (this.labels.planOneTimeDetail || '').replace('[price]', oneTime?.dataset.variantPrice || ''),
    };

    for (const input of this.planInputs) {
      const option = input.closest('[data-floating-plan-option]');
      const selected = input.value === chosen;
      input.checked = selected;
      option?.classList.toggle('is-selected', selected);
      if (input.value === 'one_time_purchase' && option) option.hidden = !oneTime;

      const detail = option?.querySelector('[data-floating-plan-detail]');
      if (detail && details[input.value]) detail.textContent = details[input.value];
    }

    if (this.planLabel) {
      this.planLabel.textContent = chosen === 'one_time_purchase' ? this.labels.planOneTime : this.labels.planSubscribe;
    }
  }

  get purchaseOptionLabel() {
    const planForm = this.planForm;
    const radio = planForm?.querySelector('input[type="radio"]:checked');
    if (!radio) return '';

    const labelSelector =
      radio.dataset.radioType === 'selling_plan' ? '.product-sub__title-subscribe' : '.product-sub__title-otp';
    return planForm.querySelector(labelSelector)?.textContent.trim() || '';
  }

  updateSubtitle() {
    if (!this.subtitleEl) return;

    const parts = [this.selectedVariant?.options_label, this.purchaseOptionLabel].filter(Boolean);
    this.subtitleEl.textContent = parts.join(' · ');
  }

  /**
   * Copies the main ATC label (with its price and struck compare-at) into the
   * bar. The price hooks are renamed on the copy: selling plans and stack & save
   * find the main button's price with querySelector, so a second
   * [data-atc-price] in the page must never exist.
   * @returns {boolean} Whether a priced label was copied
   */
  mirrorMainAtcLabel() {
    const mainText = this.mainAtcButton?.querySelector('.product-form__button-text');
    if (!mainText?.querySelector('[data-atc-price]')) return false;

    const copy = mainText.cloneNode(true);
    for (const el of copy.querySelectorAll('[data-atc-price]')) {
      el.removeAttribute('data-atc-price');
      el.classList.add('floatingCTA__price');
    }
    for (const el of copy.querySelectorAll('[data-atc-compare-at]')) {
      el.removeAttribute('data-atc-compare-at');
      el.classList.add('floatingCTA__compare');
    }
    this.atcText.replaceChildren(...copy.childNodes);
    return true;
  }

  updateCtaState() {
    const variant = this.selectedVariant;

    this.atcButton.hidden = true;
    this.externalLink.hidden = true;
    this.bisLink.hidden = true;

    if (variant?.availability === 'external' && variant.external_url) {
      this.externalLink.href = variant.external_url;
      this.externalLink.textContent = variant.external_text || this.labels.amazon;
      this.externalLink.hidden = false;
      return;
    }

    if (variant && this.shouldShowBis(variant)) {
      this.bisLink.hidden = false;
      return;
    }

    this.atcButton.hidden = false;

    if (this.isAdding) {
      this.atcText.textContent = this.labels.adding;
      this.atcButton.disabled = true;
      return;
    }

    if (variant && !variant.available) {
      this.atcText.textContent = this.labels.soldOut;
      this.atcButton.disabled = true;
      return;
    }

    if (!this.mirrorMainAtcLabel()) this.atcText.textContent = this.labels.add;
    this.atcButton.disabled = Boolean(this.mainAtcButton?.disabled) || !variant;
  }
}

if (!customElements.get('pdp-floating-cta')) {
  customElements.define('pdp-floating-cta', PdpFloatingCta);
}
